import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { fetchProjectData } from "../../features/ProjectSlice/ProjectSlice";
import { IconLocation } from "../../Icons/Icon";
import { ReadMore } from "../../Buttons/Buttons";
import Loading from "../Loading";
import CountryTabs from "../Tabs/Tabs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const imageVariants = {
  hover: {
    scale: 1.03,
    transition: {
      duration: 0.4,
      ease: [0.33, 1, 0.68, 1],
    },
  },
};

const contentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

function Project() {
  const [countryId, setCountryId] = useState("all");
  const dispatch = useDispatch();
  const [perPage, setPerPage] = useState(6);
  const { project, loading } = useSelector((state) => state.project);
  const { i18n } = useTranslation();
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop + 100 >=
      document.documentElement.offsetHeight &&
      project?.length >= perPage
    ) {
      setPerPage((prev) => prev + 6);
    }
  }, [project?.length, perPage]);

  useEffect(() => {
    dispatch(fetchProjectData({ perPage, countryId }));
  }, [dispatch, perPage, i18n.language, countryId]);
  
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  if (!project) return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 p-8">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-[480px] bg-gray-100 rounded-2xl animate-pulse" />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="flex justify-center ">
        <CountryTabs setCountryId={setCountryId} />
      </div>
      <motion.div
        className="max-w-7xl mt-3 mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        initial="hidden"
        animate="visible"
        variants={contentVariants}
      >
        <AnimatePresence>
          {project?.map((projectItem) => (
            <Link to={`/project/${projectItem.id}`}>
              <motion.div
                key={projectItem.id}
                variants={cardVariants}
                whileHover="hover"
                className="group relative bg-white  rounded-md overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col"
                exit={{ opacity: 0 }}
              >
                {/* Image Container */}
                <motion.div
                  className="relative overflow-hidden rounded-t-2xl"
                  style={{ aspectRatio: '16/9' }}
                  variants={imageVariants}
                >
                  <motion.img
                    loading="lazy"
                    src={process.env.REACT_APP_IMG_URL + projectItem.image}
                    alt={projectItem.name}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1 }}
                    whileHover={{
                      scale: 1.05,
                      transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
                    }}
                    transition={{ duration: 0.3 }}
                  />

                </motion.div>

                {/* Content Container */}
                <div className="flex-1 flex flex-col p-6">
                  <motion.div className="flex-1" variants={contentVariants}>
                    <motion.div variants={itemVariants}>
                      <h3 className="text-2xl font-bold text-slate-800 mb-3">
                        {projectItem.name}
                      </h3>
                    </motion.div>

                    <motion.div
                      className="text-slate-600 text-sm mb-4"
                      variants={itemVariants}
                    >
                      <div
                        className="break-words"
                        dangerouslySetInnerHTML={{
                          __html: projectItem.description.toString().slice(0, 75) + "...",
                        }}
                      />
                    </motion.div>
                  </motion.div>

                  <motion.div variants={itemVariants} className="mt-auto">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ReadMore
                        link={`/project/${projectItem.id}`}
                        className="text-emerald-600 hover:text-emerald-700"
                      />
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            </Link>
          ))}
        </AnimatePresence>
      </motion.div>

      {loading && <Loading />}
    </div>
  );
}

export default Project;